import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { notifications } from "./notifications";

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.VUE_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.VUE_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.VUE_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.VUE_APP_FIREBASE_APP_ID,
  measurementId: process.env.VUE_APP_FIREBASE_MEASUREMENT_ID,
};

const VAPID_KEY =
  "BKrvL0eSK-PUBFjp6KrpviFuLCogTtkIT3Vd0WJGL7EZ1lgP14ndf4qtL2Si-dUalGV8dKN2WAIPjCrm2I23hrU";

const app = initializeApp(firebaseConfig);

/**
 * Web FCM requires a secure browsing context (HTTPS, or localhost in dev).
 * http://192.168.x.x/ is neither — getMessaging throws on import and can blank the app.
 */
function getMessagingWhenAvailable() {
  if (typeof window === "undefined" || !window.isSecureContext) {
    return null;
  }
  try {
    return getMessaging(app);
  } catch {
    return null;
  }
}

const messaging = getMessagingWhenAvailable();

let listening = false;
let inFlight = null;

function attachForegroundListener() {
  if (!messaging || listening) return;
  listening = true;
  onMessage(messaging, (payload) => {
    const data = payload.data || {};
    notifications.add({
      title: payload.notification?.title,
      body: payload.notification?.body,
      qr: data.qrCode || null,
      origin: data.origin || null,
    });
  });
}

/**
 * Register SW, mint an FCM token, and subscribe onMessage once.
 * Safe to call on every logged-in session (refresh on Inventory included).
 * Never throws — callers must not block the UI on FCM.
 */
export async function ensureWebPush() {
  if (!messaging || typeof window === "undefined" || !window.isSecureContext) {
    return { token: null };
  }
  if (!("serviceWorker" in navigator)) {
    return { token: null };
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
      );
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      if (token) {
        localStorage.setItem("fcmWebToken", token);
      }
      attachForegroundListener();
      return { token: token || null };
    } catch {
      return { token: null };
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
